import React, { useState, useContext, useEffect, useId } from 'react';
import PropTypes from 'prop-types';
import { Text, Pressable, View, FlatList } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import {
    packshotTitleHeight,
    getAssetSubtitle,
    getAssetGenre,
    ITEM_TYPES,
    isTvLikeType,
    isLiveTv,
    isVod
} from 'Utils';
import { useStore } from 'Store';
import { isContentBlocked, isContentLocked } from 'Utils/parentalControlUtils';
import { Device } from 'DeviceUtils';
import LineIndicator from '../LineIndicator';
import { LockedContent } from './components';
import { ConfirmationModalContext } from '../ConfirmationModal';
import defaultStyles from './Packshot.jss';
import { CachedImage } from '../CachedImage';
import { LabelItem } from './components/LabelItem';
import { ItemDivider } from './components/ItemDivider';

const Packshot = ({
    item,
    style,
    imageUri,
    progress,
    title,
    onPress,
    subTitle,
    iconUri,
    imageDimensions,
    placeholderImage,
    parentalRating,
    hideImage,
    packageImageUrl,
    forceHideGradient,
    showMetadata
}) => {
    // Use state here until FastImage will have "placeholder" prop in future version
    const [usedImage, setUsedImage] = useState({
        uri: imageUri
    });
    const [isChannel, setIsChannel] = useState(false);

    const { parentalLockConfig } = useStore();
    const { setProtectedContent } = useContext(ConfirmationModalContext);

    const onImageError = () => {
        setUsedImage(placeholderImage);
    };

    const adultLock = isContentLocked(item.isAdult(), parentalLockConfig);
    const ageBlock = isContentBlocked(parentalRating, parentalLockConfig);

    const containerId = useId();

    useEffect(() => {
        setUsedImage({ uri: imageUri });
    }, [imageUri]);

    useEffect(() => {
        item.modelType === ITEM_TYPES.CHANNELS && setIsChannel(true);
    }, [item.modelType]);

    const hideGradient =
        forceHideGradient || !(isLiveTv(item) || item.modelType === ITEM_TYPES.TV_SERIES);
    // If metadata is on, we must slightly enlarge container
    const height = imageDimensions.height + packshotTitleHeight + (showMetadata ? 30 : 0);

    return (
        <Pressable
            onPress={(event) => {
                if (ageBlock || adultLock) {
                    setProtectedContent(item, () => onPress({ data: {}, event }));
                } else {
                    onPress({ data: {}, event });
                }
            }}
            style={[
                defaultStyles.container,
                {
                    width: imageDimensions.width,
                    height
                },
                style
            ]}
        >
            <View style={defaultStyles.container}>
                {adultLock ? (
                    <LockedContent
                        adult
                        dimension={{
                            width: imageDimensions.width,
                            height: imageDimensions.height
                        }}
                    />
                ) : (
                    <View key={containerId}>
                        <View style={defaultStyles.itemImageContainer}>
                            {Boolean(iconUri) && !hideImage && (
                                <CachedImage
                                    style={defaultStyles.icon}
                                    source={{ uri: iconUri }}
                                    resizeMode="contain"
                                />
                            )}
                            {packageImageUrl ? (
                                <CachedImage
                                    source={{ uri: packageImageUrl }}
                                    style={defaultStyles.packageIcon}
                                    resizeMode="contain"
                                />
                            ) : null}
                            {!hideImage && (
                                <>
                                    <CachedImage
                                        key={imageUri}
                                        style={{
                                            width: imageDimensions.width,
                                            height: imageDimensions.height,
                                            backgroundColor: isChannel ? '#2E2F31' : 'transparent',
                                            paddingVertical: 10
                                        }}
                                        //defaultSource={placeholderImage}
                                        loadingIndicatorSource={placeholderImage}
                                        source={usedImage}
                                        onError={onImageError}
                                        resizeMode="contain"
                                    />
                                    {isVod(item) &&
                                        'getLabels' in item &&
                                        item.getLabels().length > 0 && (
                                            <FlatList
                                                style={defaultStyles.labelConteiner}
                                                data={item?.data?.labels}
                                                renderItem={(flatListItem) => (
                                                    <LabelItem label={flatListItem.item} />
                                                )}
                                                // keyExtractor={(data) => data?.item}
                                                keyExtractor={(data, index) =>
                                                    `${data?.item}-${index}`
                                                }
                                                ItemSeparatorComponent={ItemDivider}
                                                listKey={() => item.data.labels}
                                            />
                                        )}
                                    {!hideGradient && (
                                        <LinearGradient
                                            colors={[
                                                '#010101CD',
                                                '#01010177',
                                                'transparent',
                                                'transparent',
                                                'transparent'
                                            ]}
                                            style={defaultStyles.imageGradient}
                                        />
                                    )}
                                </>
                            )}
                            {progress > 0 && (
                                <LineIndicator
                                    style={defaultStyles.indicator}
                                    borderRadius={1}
                                    progress={progress}
                                />
                            )}
                        </View>
                        <Text
                            style={defaultStyles.itemText}
                            numberOfLines={subTitle || showMetadata ? 1 : 2}
                        >
                            {title}
                        </Text>
                        {Boolean(subTitle) && (
                            <Text style={defaultStyles.itemSubText} numberOfLines={1}>
                                {subTitle}
                            </Text>
                        )}
                        {showMetadata && [
                            (isTvLikeType(item) || item.modelType === ITEM_TYPES.TV_SERIES) && (
                                <Text style={defaultStyles.metadataText}>
                                    {getAssetSubtitle(item)}
                                </Text>
                            ),
                            <Text key="assetGenre" style={defaultStyles.metadataText}>
                                {getAssetGenre(item, item.locale)}
                            </Text>
                        ]}

                        {ageBlock && (
                            <LockedContent
                                style={defaultStyles.ageRestrictedStyle}
                                dimension={{
                                    width: imageDimensions.width,
                                    height: imageDimensions.height
                                }}
                            />
                        )}
                    </View>
                )}
            </View>
        </Pressable>
    );
};

Packshot.defaultProps = {
    style: {},
    progress: null,
    onPress: () => {},
    iconUri: null,
    subTitle: null,
    placeholderImage: null,
    parentalRating: null,
    packageImageUrl: null,
    hideImage: false,
    forceHideGradient: false,
    showMetadata: false,
    title: undefined
};

Packshot.propTypes = {
    item: PropTypes.shape({}).isRequired,
    style: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.number]),
    imageUri: PropTypes.string.isRequired,
    progress: PropTypes.number,
    title: PropTypes.string,
    onPress: PropTypes.func,
    subTitle: PropTypes.string,
    iconUri: PropTypes.string,
    imageDimensions: PropTypes.shape({
        width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        height: PropTypes.number.isRequired
    }).isRequired,
    placeholderImage: Device.isWeb
        ? PropTypes.any
        : PropTypes.oneOfType([
              PropTypes.number,
              PropTypes.shape({
                  // for WEB
                  data: PropTypes.any
              })
          ]),
    packageImageUrl: PropTypes.oneOfType([PropTypes.string]),
    parentalRating: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
    hideImage: PropTypes.bool,
    forceHideGradient: PropTypes.bool,
    showMetadata: PropTypes.bool
};

export default React.memo(Packshot);
